
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Search, HelpCircle, User, BookOpen, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      setUser(user);
      
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        setProfile(data);
      }
    };
    
    fetchUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user);
      if (session?.user) {
        // Using setTimeout to avoid Supabase auth deadlock
        setTimeout(async () => {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
          
          setProfile(data);
        }, 0);
      } else {
        setProfile(null);
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const getInitials = () => {
    if (profile) {
      return `${profile.first_name?.charAt(0) || ''}${profile.last_name?.charAt(0) || ''}`;
    }
    return "U";
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
      navigate('/');
    } catch (error) {
      toast({
        title: "Sign out failed",
        description: "There was an error signing out.",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="bg-white border-b border-gray-100">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo - Left justified */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/lovable-uploads/58129023-c293-4dcb-ae66-69419beb90bd.png" 
              alt="Alumni Insights" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Right justified */}
          <div className="hidden md:flex items-center space-x-6 ml-auto">
            <Link
              to="/services"
              className="text-gray-600 hover:text-crimson transition-colors font-medium flex items-center gap-2"
            >
              Services
            </Link>
            <Link
              to="/how-it-works"
              className="text-gray-600 hover:text-crimson transition-colors font-medium flex items-center gap-2"
            >
              <HelpCircle size={18} />
              How It Works
            </Link>
            <Link
              to="/browse"
              className="text-gray-600 hover:text-crimson transition-colors font-medium flex items-center gap-2"
            >
              <Search size={18} />
              Browse Alumni
            </Link>
            <Link
              to="/blog"
              className="text-gray-600 hover:text-crimson transition-colors font-medium flex items-center gap-2"
            >
              <BookOpen size={18} />
              Blog
            </Link>
            {user && (
              <Link
                to="/messages"
                className="text-gray-600 hover:text-crimson transition-colors font-medium"
              >
                Messages
              </Link>
            )}
          </div>

          {/* Auth or User Menu */}
          <div className="hidden md:flex items-center space-x-4 ml-6">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="text-right mr-2 hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">{profile?.first_name} {profile?.last_name}</p>
                  <p className="text-xs text-gray-500">{profile?.user_type === 'alumni' ? 'Alumni' : 'Student'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Link to="/dashboard">
                    <Avatar className="cursor-pointer">
                      <AvatarImage src={profile?.avatar_url} />
                      <AvatarFallback className="bg-crimson text-white">{getInitials()}</AvatarFallback>
                    </Avatar>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleSignOut}
                    className="text-gray-500 hover:text-crimson"
                  >
                    <LogOut size={18} />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button variant="outline" className="border-crimson text-crimson" onClick={() => navigate("/auth")}>
                  Sign In
                </Button>
                <Button className="bg-crimson hover:bg-crimson-dark" onClick={() => navigate("/auth?tab=register")}>
                  Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-[250px] sm:w-[300px]">
                <div className="flex flex-col gap-6 mt-8">
                  {user && (
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                      <Avatar>
                        <AvatarImage src={profile?.avatar_url} />
                        <AvatarFallback className="bg-crimson text-white">{getInitials()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{profile?.first_name} {profile?.last_name}</p>
                        <Link to="/dashboard" className="text-sm text-crimson">
                          View Dashboard
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  <Link
                    to="/services"
                    className="text-lg font-medium text-gray-600 hover:text-crimson transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Services
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="text-lg font-medium text-gray-600 hover:text-crimson transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <HelpCircle size={18} />
                    How It Works
                  </Link>
                  <Link
                    to="/browse"
                    className="text-lg font-medium text-gray-600 hover:text-crimson transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Search size={18} />
                    Browse Alumni
                  </Link>
                  <Link
                    to="/blog"
                    className="text-lg font-medium text-gray-600 hover:text-crimson transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <BookOpen size={18} />
                    Blog
                  </Link>
                  
                  {user && (
                    <Link
                      to="/messages"
                      className="text-lg font-medium text-gray-600 hover:text-crimson transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Messages
                    </Link>
                  )}
                  
                  {!user && (
                    <div className="flex flex-col gap-3 mt-4">
                      <Button variant="outline" className="w-full border-crimson text-crimson" onClick={() => navigate("/auth")}>
                        Sign In
                      </Button>
                      <Button className="w-full bg-crimson hover:bg-crimson-dark" onClick={() => navigate("/auth?tab=register")}>
                        Sign Up
                      </Button>
                    </div>
                  )}
                  {user && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={async () => {
                        await handleSignOut();
                        setIsMenuOpen(false);
                      }}
                    >
                      Sign Out
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
